/* ---- journey stage: one point system that morphs through the stages ----
   Ships with the theme. It draws on top of the journey's own tiles and hides
   them (`data-gl="on"`), so a browser with no WebGL, no three.js or a
   reduce-motion preference keeps the stills and the copy and loses nothing but
   the motion. The scroll maths is theme.js's own, so the geometry and the copy
   change on the same beat.

   SHAPES is positional: entry k is the formation for stage block k, in
   block_order. Adding, removing or reordering a stage in the theme editor means
   adding, removing or reordering a formation here to match — otherwise the copy
   advances through one count and the points through another, and they drift
   apart over the scroll. SHAPES.length and the number of stage blocks must be
   equal; right now that is 7, in this order:

     shift / chat / translate / image / video / music / avatar

   Note that music comes before avatar, which is not the order the functions are
   defined in below. The array is the source of truth, not the file order. */(function () {
  var root = document.querySelector('[data-journey]');
  if (!root || !window.THREE) return;
  var media = root.querySelector('.journey__media');
  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  var canvas = document.createElement('canvas');
  canvas.className = 'journey__gl';
  canvas.setAttribute('aria-hidden', 'true');
  media.insertBefore(canvas, media.firstChild);

  var renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
  } catch (e) { canvas.remove(); return; }
  if (!renderer.getContext()) { canvas.remove(); return; }
  media.setAttribute('data-gl', 'on');

  var N = innerWidth < 700 ? 3800 : 5200;
  var scene = new THREE.Scene();
  var camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
  camera.position.set(0, 0, 4.3);
  var group = new THREE.Group();
  scene.add(group);

  /* ---- the formations ---- */
  function orb(i, n, o) {                       // shift · one workspace
    var y = 1 - (i / (n - 1)) * 2, r = Math.sqrt(Math.max(0, 1 - y * y));
    var th = i * 2.399963;
    var k = 1.35 + (Math.sin(i * 12.9898) * 0.5 + 0.5) * 0.12;
    o[0] = Math.cos(th) * r * k; o[1] = y * k; o[2] = Math.sin(th) * r * k;
  }

  /* chat.
     One speech bubble, read instantly. Two abstract passes were tried first —
     bubbles in a stack, then a transcript of ragged type — and both were
     rejected, so this is the plain thing: a rounded-rectangle outline with a
     tail at the bottom left and three dots inside it.

     A generic bubble, not a copy of anyone's logo — no handset, no brand mark,
     no brand colour. The points take the skin's accent like every other stage.

     Outline is a superellipse: x = hw · sign(cosθ) · |cosθ|^P. P is the only
     dial — 0.55 here, lower is boxier, higher rounder toward an ellipse. Angle
     parameterisation bunches points slightly at the corners, which suits a
     point cloud: the corners are where the eye reads the shape. */
  function chat(i, n, o) {
    var edgeN = Math.floor(n * 0.56);
    var tailN = Math.floor(n * 0.08);
    var dotsN = n - edgeN - tailN;
    var hw = 1.05, hh = 0.78, P = 0.55;

    if (i < edgeN) {                           // the bubble
      var th = (i / edgeN) * 6.2831853;
      var ct = Math.cos(th), st = Math.sin(th);
      var sx = ct < 0 ? -1 : 1, sy = st < 0 ? -1 : 1;
      o[0] = hw * sx * Math.pow(Math.abs(ct), P) + rnd(i, 1) * 0.014;
      o[1] = hh * sy * Math.pow(Math.abs(st), P) + rnd(i, 2) * 0.014;
      o[2] = rnd(i, 3) * 0.05;
      return;
    }

    if (i < edgeN + tailN) {                   // the tail, filled
      var k = i - edgeN;
      var u = Math.abs(rnd(k, 11)), v = Math.abs(rnd(k, 12));
      if (u + v > 1) { u = 1 - u; v = 1 - v; } // fold back into the triangle
      var ax = -0.60, ay = -0.72;
      var bx = -0.88, by = -1.16;
      var cx = -0.26, cy = -0.74;
      o[0] = ax + u * (bx - ax) + v * (cx - ax);
      o[1] = ay + u * (by - ay) + v * (cy - ay);
      o[2] = rnd(k, 13) * 0.04;
      return;
    }

    var m = i - edgeN - tailN, D = 3;          // three dots
    var per = Math.floor(dotsN / D);
    var d = Math.min(D - 1, (m / per) | 0), j = m - d * per;
    var r = Math.sqrt(j / per) * 0.145;        // sqrt for an even disc
    var a = j * 2.399963;                      // golden angle
    o[0] = (d - 1) * 0.40 + Math.cos(a) * r;
    o[1] = -0.02 + Math.sin(a) * r;
    o[2] = rnd(i, 4) * 0.04;
  }

  /* translate.
     Two panels of type, upper left and lower right, with a curved arrow sweeping
     between them each way — the same thing, carried across. An exchange rather
     than a globe, deliberately: a wireframe globe would be a second sphere in a
     journey that already opens on one.

     The panels are superellipse outlines like the bubble, but small, paired and
     set on a diagonal, so the pair reads as two documents rather than as one
     speech bubble. Arrows are quadratic Béziers with a filled triangular head —
     without the head a curve is just a curve and the direction is lost. */
  function translate(i, n, o) {
    var S = 1.35;                              // fills the frame; scale last
    var edgeN = Math.floor(n * 0.30);          // the two panel outlines
    var lineN = Math.floor(n * 0.16);          // type inside them
    var arcN  = Math.floor(n * 0.36);          // the two arrows
    var hw = 0.42, hh = 0.30, P = 0.42;
    var AX = -0.62, AY = 0.34, BX = 0.62, BY = -0.34;
    var x = 0, y = 0;

    if (i < edgeN) {                           // panels
      var half = edgeN >> 1, first = i < half;
      var k = first ? i : i - half;
      var per = first ? half : edgeN - half;
      var th = (k / per) * 6.2831853;
      var ct = Math.cos(th), st = Math.sin(th);
      var sx = ct < 0 ? -1 : 1, sy = st < 0 ? -1 : 1;
      x = (first ? AX : BX) + hw * sx * Math.pow(Math.abs(ct), P) + rnd(i, 1) * 0.010;
      y = (first ? AY : BY) + hh * sy * Math.pow(Math.abs(st), P) + rnd(i, 2) * 0.010;
      o[0] = x * S; o[1] = y * S; o[2] = rnd(i, 3) * 0.05;
      return;
    }

    if (i < edgeN + lineN) {                   // two short lines per panel
      var m = i - edgeN, q = Math.max(1, Math.floor(lineN / 4));
      var l = Math.min(3, (m / q) | 0), j = m - l * q;
      var second = l >= 2, row = l % 2;
      var w = row === 0 ? 0.26 : 0.17;         // ragged, so it reads as type
      x = (second ? BX : AX) - w + (j / q) * w * 2 + rnd(i, 4) * 0.008;
      y = (second ? BY : AY) + (row === 0 ? 0.07 : -0.07) + rnd(i, 5) * 0.006;
      o[0] = x * S; o[1] = y * S; o[2] = rnd(i, 6) * 0.03;
      return;
    }

    if (i < edgeN + lineN + arcN) {            // the arrows
      var a = i - edgeN - lineN, halfA = arcN >> 1, fwd = a < halfA;
      var kk = fwd ? a : a - halfA;
      var pp = fwd ? halfA : arcN - halfA;
      var t = kk / pp, u = 1 - t;
      var p0x, p0y, c1x, c1y, p1x, p1y;
      if (fwd) { p0x = -0.20; p0y = 0.34; c1x = 0.30;  c1y = 0.66;  p1x = 0.62;  p1y = 0.00; }
      else     { p0x = 0.20;  p0y = -0.34; c1x = -0.30; c1y = -0.66; p1x = -0.62; p1y = 0.00; }
      x = u * u * p0x + 2 * u * t * c1x + t * t * p1x;
      y = u * u * p0y + 2 * u * t * c1y + t * t * p1y;
      o[0] = (x + rnd(i, 7) * 0.012) * S;
      o[1] = (y + rnd(i, 8) * 0.012) * S;
      o[2] = rnd(i, 9) * 0.05;
      return;
    }

    var h = i - edgeN - lineN - arcN;          // the two arrowheads, filled
    var headN = n - edgeN - lineN - arcN;
    var down = h < (headN >> 1);
    var uu = Math.abs(rnd(h, 11)), vv = Math.abs(rnd(h, 12));
    if (uu + vv > 1) { uu = 1 - uu; vv = 1 - vv; }
    var ax, ay, bx, by, cx, cy;
    if (down) { ax = 0.62;  ay = -0.08; bx = 0.53;  by = 0.09;  cx = 0.71;  cy = 0.09; }
    else      { ax = -0.62; ay = 0.08;  bx = -0.53; by = -0.09; cx = -0.71; cy = -0.09; }
    x = ax + uu * (bx - ax) + vv * (cx - ax);
    y = ay + uu * (by - ay) + vv * (cy - ay);
    o[0] = x * S; o[1] = y * S; o[2] = rnd(h, 13) * 0.04;
  }

  function grid(i, n, o) {                     // image
    /* What image generation does: noise resolving into structure. The right-hand
       cells are tight dots, the left-hand ones have dispersed into a cloud. The
       points are grouped into cells first — a per-point lattice is finer than the
       points themselves and just reads as more noise. */
    var COLS = 22, ROWS = 14, CELLS = COLS * ROWS;
    var c = i % CELLS;                         // round robin: every cell gets its share
    var col = c % COLS, row = (c / COLS) | 0;
    var u = col / (COLS - 1), w = row / (ROWS - 1);

    var order = Math.max(0, Math.min(1, (u - 0.12) / 0.62));
    order = order * order * (3 - 2 * order);   // ease, so the middle is the transition
    var spread = 0.022 + (1 - order) * 0.34;
    var x = (u - 0.5) * 2.90 + rnd(i, 1) * spread;
    var y = (w - 0.5) * 1.85 + rnd(i, 2) * spread;
    var z = rnd(i, 3) * spread * 1.7 * (1 - order);

    var A = 0.24, ca = Math.cos(A), sa = Math.sin(A);
    o[0] = x * ca + z * sa;
    o[1] = y;
    o[2] = -x * sa + z * ca;
  }

  function frames(i, n, o) {                   // video
    /* Frames through time: outlines, not filled planes — a stack of filled
       planes just read as the image lattice again. Each frame sits further back,
       a little smaller and a little more turned, so the run reads as motion. */
    var F = 7, per = Math.floor(n / F);
    var f = Math.min(F - 1, (i / per) | 0), j = i - f * per;
    var t = f / (F - 1);
    var sc = 0.90 - t * 0.30;
    var hw = 1.02 * sc, hh = 0.64 * sc;

    var u = (j / per) * 4;                     // walk the perimeter
    var side = u | 0, k = u - side, x, y;
    if (side === 0)      { x = -hw + k * 2 * hw; y = hh; }
    else if (side === 1) { x = hw;  y = hh - k * 2 * hh; }
    else if (side === 2) { x = hw - k * 2 * hw; y = -hh; }
    else                 { x = -hw; y = -hh + k * 2 * hh; }

    var jit = 0.012;
    x += rnd(i, 1) * jit; y += rnd(i, 2) * jit;

    var rot = t * 0.46;                        // the twist that makes it move
    var c = Math.cos(rot), sn = Math.sin(rot);
    o[0] = x * c - y * sn;
    o[1] = x * sn + y * c;
    o[2] = 1.15 - t * 3.15;
  }

  function bars(i, n, o) {                      // music
    var B = 26, per = Math.floor(n / B), b = Math.min(B - 1, (i / per) | 0), j = i - b * per;
    var h = 0.28 + Math.abs(Math.sin(b * 0.9) * Math.cos(b * 0.37)) * 1.25;
    var t = j / per;
    o[0] = (b / (B - 1) - 0.5) * 3.0;
    o[1] = (t - 0.5) * 2 * h * 0.5 + (h * 0.5 - 0.62);
    o[2] = rnd(i, 3) * 0.16;
  }

  /* avatar and voice.
     Not a head. Four attempts at modeling one all read as a mannequin, so this
     drops the likeness and draws what an avatar actually is in the product: a
     portrait ring with a voice coming out of it. A dense ring, a sparse field
     inside it so the ring still reads as the edge, and four arcs opening to the
     right — speech leaving the frame. */
  function avatar(i, n, o) {
    var CX = -0.30;                            // the ring sits left of center so
                                               // the arcs have room to open
    var ringN = Math.floor(n * 0.46);
    var discN = Math.floor(n * 0.14);
    var waveN = n - ringN - discN;

    if (i < ringN) {                           // the ring
      var t = i / ringN;
      var a = t * 6.2831853 * 7.0;             // wound several times for density
      var rr = 0.76 + rnd(i, 1) * 0.042;
      o[0] = Math.cos(a) * rr + CX;
      o[1] = Math.sin(a) * rr;
      o[2] = rnd(i, 2) * 0.07;
      return;
    }

    if (i < ringN + discN) {                   // the field inside it
      var j2 = i - ringN, u = j2 / discN;
      var r2 = Math.sqrt(u) * 0.64;
      var th = j2 * 2.399963;                  // golden angle, even fill
      o[0] = Math.cos(th) * r2 + CX;
      o[1] = Math.sin(th) * r2;
      o[2] = rnd(i, 3) * 0.12;
      return;
    }

    var k = i - ringN - discN, W = 4;          // the voice
    var per = Math.floor(waveN / W);
    var w = Math.min(W - 1, (k / per) | 0), m = k - w * per;
    var v = m / per;
    var spread = 0.40 + w * 0.10;              // each arc opens a little wider
    var ang = (v - 0.5) * spread * Math.PI;
    var rad = 0.98 + w * 0.21;
    o[0] = Math.cos(ang) * rad + CX;
    o[1] = Math.sin(ang) * rad;
    o[2] = rnd(i, 4) * 0.06;
  }

  function rnd(i, k) { var x = Math.sin(i * 127.1 + k * 311.7) * 43758.5453; return (x - Math.floor(x)) * 2 - 1; }

  /* Matches block_order exactly: shift / chat / translate / image / video /
     music / avatar. */
  var SHAPES = [orb, chat, translate, grid, frames, bars, avatar];
  var forms = SHAPES.map(function (fn) {
    var a = new Float32Array(N * 3), o = [0, 0, 0];
    for (var i = 0; i < N; i++) { fn(i, N, o); a[i * 3] = o[0]; a[i * 3 + 1] = o[1]; a[i * 3 + 2] = o[2]; }
    return a;
  });

  var seeds = new Float32Array(N);
  for (var i = 0; i < N; i++) seeds[i] = i / N;

  var geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(forms[0].slice(), 3));
  geo.setAttribute('aFrom', new THREE.BufferAttribute(forms[0].slice(), 3));
  geo.setAttribute('aTo', new THREE.BufferAttribute(forms[0].slice(), 3));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));

  var uniforms = {
    uMix: { value: 1 }, uTime: { value: 0 }, uSize: { value: 2.4 },
    uDpr: { value: 1 }, uAlpha: { value: 0.9 },
    uA: { value: new THREE.Color('#5B8CFF') }, uB: { value: new THREE.Color('#56CCF8') }
  };

  var mat = new THREE.ShaderMaterial({
    uniforms: uniforms, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: [
      'attribute vec3 aFrom;', 'attribute vec3 aTo;', 'attribute float aSeed;',
      'uniform float uMix; uniform float uTime; uniform float uSize; uniform float uDpr;',
      'varying float vShade;',
      'void main(){',
      '  vec3 p = mix(aFrom, aTo, uMix);',
      '  float s = aSeed * 6.2831;',
      '  p += vec3(sin(uTime*0.55 + s*3.1), cos(uTime*0.47 + s*4.3), sin(uTime*0.39 + s*2.2)) * 0.038;',
      '  vec4 mv = modelViewMatrix * vec4(p, 1.0);',
      '  gl_PointSize = uSize * uDpr * (3.4 / -mv.z);',
      '  gl_Position = projectionMatrix * mv;',
      '  vShade = clamp((p.y + 1.5) / 3.0, 0.0, 1.0);',
      '}'
    ].join('\n'),
    fragmentShader: [
      'uniform vec3 uA; uniform vec3 uB; uniform float uAlpha;',
      'varying float vShade;',
      'void main(){',
      '  vec2 d = gl_PointCoord - 0.5;',
      '  float r = dot(d, d);',
      '  if (r > 0.25) discard;',
      '  float a = smoothstep(0.25, 0.015, r);',
      '  gl_FragColor = vec4(mix(uA, uB, vShade), a * uAlpha);',
      '}'
    ].join('\n')
  });

  var points = new THREE.Points(geo, mat);
  group.add(points);

  /* ---- colors follow the skin, the same tokens the rest of the page reads ---- */
  var lightGround = false, sized = false;
  function syncTheme() {
    var cs = getComputedStyle(document.documentElement);
    var accent = (cs.getPropertyValue('--accent') || '#5B8CFF').trim();
    var sky = (cs.getPropertyValue('--sky') || cs.getPropertyValue('--accent-hover') || '#56CCF8').trim();
    var bg = (cs.getPropertyValue('--bg') || '#0E1628').trim();
    try { uniforms.uA.value.set(accent); uniforms.uB.value.set(sky); } catch (e) {}
    var c = new THREE.Color(bg);
    lightGround = (c.r * 0.299 + c.g * 0.587 + c.b * 0.114) > 0.55;
    mat.blending = lightGround ? THREE.NormalBlending : THREE.AdditiveBlending;
    uniforms.uAlpha.value = 1.0;
    mat.needsUpdate = true;
    if (sized) resize();
  }
  syncTheme();
  new MutationObserver(syncTheme).observe(document.documentElement,
    { attributes: true, attributeFilter: ['data-skin', 'data-theme', 'data-mode'] });

  /* ---- size ---- */
  function resize() {
    var r = root.querySelector('[data-journey-viewport]').getBoundingClientRect();
    var w = Math.max(1, r.width), h = Math.max(1, r.height);
    var dpr = Math.min(devicePixelRatio || 1, 2);
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    uniforms.uDpr.value = dpr;
    var base = w < 700 ? 2.5 : 2.8;
    uniforms.uSize.value = lightGround ? base * 1.36 : base;
    camera.aspect = w / h;
    camera.position.z = w < 700 ? 5.2 : 4.3;
    camera.updateProjectionMatrix();
  }
  sized = true;
  resize();
  addEventListener('resize', resize);

  /* ---- stage tracking: theme.js's own maths, so copy and geometry agree ---- */
  var HOLD = 0.42, damped = 0, stage = 0, mixFrom = 0, mixTo = 0, mixT = 1;
  function eased(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
  function readTarget() {
    var rect = root.getBoundingClientRect();
    var total = rect.height - innerHeight;
    if (total <= 0) return 0;
    return Math.max(0, Math.min(1, (-rect.top) / total));
  }
  function setStage(idx) {
    if (idx === stage) return;
    var from = geo.getAttribute('aFrom'), to = geo.getAttribute('aTo');
    var cur = forms[mixFrom], nxt = forms[mixTo], m = mixT;
    for (var i = 0; i < N * 3; i++) from.array[i] = cur[i] + (nxt[i] - cur[i]) * m;
    to.array.set(forms[idx]);
    from.needsUpdate = to.needsUpdate = true;
    mixFrom = idx; mixTo = idx; mixT = 0; stage = idx;
    uniforms.uMix.value = 0;
    morph = 0;
  }
  var morph = 1;
  geo.getAttribute('aTo').array.set(forms[0]);

  /* On the storefront the journey is either on this page or it is not, so the
     preview's hidden-route test becomes an ordinary visibility test: stop
     rendering while the section is off screen or the tab is in the background,
     rather than holding a GPU for the whole scroll. */
  var onScreen = true;
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (es) {
      es.forEach(function (e) { onScreen = e.isIntersecting; });
    }, { rootMargin: '200px' }).observe(root);
  }
  function visible() { return onScreen; }

  var t0 = performance.now(), raf = null;
  function frame(now) {
    raf = requestAnimationFrame(frame);
    if (!visible() || document.hidden) return;
    var dt = Math.min(0.05, (now - t0) / 1000); t0 = now;
    uniforms.uTime.value += dt;

    var target = readTarget();
    damped += (target - damped) * 0.14;
    if (Math.abs(target - damped) < 0.0005) damped = target;
    var scaled = damped * SHAPES.length;
    var idx = Math.max(0, Math.min(SHAPES.length - 1, Math.floor(scaled)));
    var within = scaled - idx;
    var hand = within <= HOLD ? 0 : eased((within - HOLD) / (1 - HOLD));
    setStage(hand > 0.5 ? Math.min(SHAPES.length - 1, idx + 1) : idx);

    if (morph < 1) { morph = Math.min(1, morph + dt / 0.85); uniforms.uMix.value = eased(morph); mixT = eased(morph); }
    var T = uniforms.uTime.value;
    group.rotation.y = Math.sin(T * 0.16) * 0.30;
    group.rotation.x = Math.sin(T * 0.21) * 0.07;
    renderer.render(scene, camera);
  }

  function still() {
    var scaled = readTarget() * SHAPES.length;
    setStage(Math.max(0, Math.min(SHAPES.length - 1, Math.floor(scaled))));
    uniforms.uMix.value = 1; morph = 1; mixT = 1;
    renderer.render(scene, camera);
  }

  if (reduced) { still(); addEventListener('scroll', still, { passive: true }); }
  else raf = requestAnimationFrame(frame);
  addEventListener('pagehide', function () { if (raf) cancelAnimationFrame(raf); });
  addEventListener('hashchange', function () { setTimeout(function () { resize(); if (reduced) still(); }, 60); });
})();
